// lib/calendarOps.mjs — R115 calendar hub, OPS builder: the pure client rules
// behind client/src/pages/calendar/ops/* (chips, stats, rails, the day agenda,
// the date-chooser forms' request bodies, Plan cleans). No DOM, no fetch —
// tested by lib/calendarOps.test.mjs; typed by calendarOps.d.mts.
//
// Inputs are the base calendar payload (CalData: stays, events.turns,
// events.work_orders, properties) and the ops layers served by
// server/calendarLayers (inspections → { jobs, inspectors }, tasks → { items },
// health → { by_listing }). A layer that answered { unavailable: true } is
// passed as null by the page — every stat then reads "—", never 0.

export const UNASSIGNED_HORIZON = 7;     // stays.js clampUnassigned / the HK 7-day rule
export const INSPECTION_MAX_DAYS = 60;   // POST /inspections/assign manual window + the move cap
export const AT_RISK_ARRIVAL_DAYS = 7;
export const TURN_TYPES = Object.freeze([['turnover', 'Turnover'], ['mid_stay', 'Mid-stay'], ['deep', 'Deep clean'], ['owner', 'Owner stay'], ['first_clean', 'First clean']]);
export const TASK_PRIORITIES = Object.freeze([['normal', 'Normal'], ['high', 'High'], ['urgent', 'Urgent'], ['low', 'Low']]);
const DONE_TURN = new Set(['cleaned', 'verified', 'ready']);

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const isDay = (s) => typeof s === 'string' && DAY_RE.test(s);
export function addDays(d, n) { const t = new Date(d + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); }
export function diffDays(a, b) { return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000); }
export function initials(name) {
  const words = String(name || '').split(/\s+/).filter(w => /[A-Za-z0-9]/.test(w)).map(w => w.replace(/[^A-Za-z0-9]/g, ''));
  if (!words.length) return '?';
  const a = words[0][0] || '';
  const b = words.length > 1 ? (words[words.length - 1][0] || '') : (words[0][1] || '');
  return (a + b).toUpperCase();
}
const md = (d) => { try { return new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }); } catch { return d; } };
const arr = (v) => (Array.isArray(v) ? v : []);
// A layer payload the page may use, or null (absent / unavailable).
export const usable = (layer) => (layer && typeof layer === 'object' && !layer.unavailable ? layer : null);

// ── inspections ─────────────────────────────────────────────────────────────
const OPEN_JOB = new Set(['unassigned', 'assigned', 'in_progress']);
export const isGhost = (j) => !!j && j.state === 'moved';
export const jobOpen = (j) => !!j && OPEN_JOB.has(j.state);
// The day a job is worked on: a missed one rolled into today sits on today.
export const jobDay = (j) => (j && j.roll_to ? j.roll_to : j ? j.date : null);
export function jobsOnDay(jobs, D) {
  const seen = new Set();
  return arr(jobs).filter(j => j && !isGhost(j) && (j.date === D || j.roll_to === D) && !seen.has(j.id) && seen.add(j.id));
}
// Unassigned inspections to place: today → today+7 (a missed one counts on today).
export function inspUnassigned(jobs, today, days = UNASSIGNED_HORIZON) {
  const end = addDays(today, days);
  return arr(jobs).filter(j => j && j.state === 'unassigned' && jobDay(j) >= today && jobDay(j) <= end);
}
export function inspKindLabel(j) {
  if (!j) return '';
  if (j.kind === 'manual') return '📌 Scheduled';
  if (j.kind === 'moved') return j.moved_from ? `↪ Moved from ${md(j.moved_from)}` : '↪ Rescheduled';
  if (j.kind === 'checkin_light') return 'Pre-arrival (light)';
  if (j.pinned) return 'Checkout · 📌';
  return j.moved_from ? `Checkout · ↪ from ${md(j.moved_from)}` : 'Checkout';
}
export function inspStateLabel(j) {
  if (!j) return '';
  const s = j.state;
  if (s === 'moved') return j.moved_to ? `Moved to ${md(j.moved_to)}` : 'Moved';
  if (s === 'not_inspected') return 'Not inspected';
  if (s === 'in_progress') return 'In progress';
  if (s === 'done') return 'Done';
  if (s === 'skipped') return 'Not possible / cancelled';
  if (j.missed) return 'Missed';
  return s === 'assigned' ? 'Assigned' : 'Needs an inspector';
}
// queue/move `kind` for a job (server INSP_MOVE_KINDS).
export function moveKind(j) {
  if (!j) return 'checkout';
  if (j.kind === 'checkout' && j.missed) return 'missed';
  return j.kind;
}
export const canMoveInspection = (j, today) => !!j && jobOpen(j) && j.state !== 'in_progress' && !isGhost(j) && (j.date >= today || !!j.missed);
export const canDropInspection = (j, date, today) => canMoveInspection(j, today) && isDay(date) && date >= today && date <= addDays(today, INSPECTION_MAX_DAYS) && date !== j.date;
// The chips one job draws (its own date + — rolled — a dashed copy on today).
export function inspChips(j, today, from, to) {
  if (!j) return [];
  const out = [];
  const base = { entity: { kind: 'inspection', id: j.id, listing_id: j.listing_id, date: j.date }, listing_id: j.listing_id, glyph: '🔍' };
  const ini = j.inspector ? [initials(j.inspector)] : undefined;
  const near = j.date >= today && j.date <= addDays(today, UNASSIGNED_HORIZON);
  let tone = 'info', dashed = false, strike = false, ring = false, label;
  switch (j.state) {
    case 'unassigned': tone = near || j.missed ? 'warn' : 'muted'; dashed = true; break;
    case 'assigned': tone = 'info'; break;
    case 'in_progress': tone = 'accent'; break;
    case 'done': tone = 'ok'; label = '✓'; break;
    case 'skipped': tone = 'muted'; strike = true; break;
    case 'moved': tone = 'muted'; dashed = true; label = j.moved_to ? `↪ ${md(j.moved_to)}` : '↪'; break;
    case 'not_inspected': tone = 'bad'; dashed = true; label = 'not inspected'; break;
    default: tone = 'muted';
  }
  if (j.missed) { tone = 'bad'; ring = true; }
  const badge = j.kind === 'manual' || j.pinned ? '📌' : j.kind === 'moved' ? '↪' : j.kind === 'checkin_light' ? '☀' : (j.orphaned ? '⚠' : undefined);
  const title = [`Inspection · ${inspKindLabel(j)}`, inspStateLabel(j), j.guest_inside ? 'A guest is staying that day' : '', j.orphaned ? 'Its booking changed or was cancelled' : '', j.walked_early ? `Walked early (${md(j.walked_early)}) — still owed after checkout` : ''].filter(Boolean).join(' · ');
  if (j.date >= from && j.date <= to) {
    out.push({ ...base, key: `insp:${j.id}`, start: j.date, tone, dashed: dashed || undefined, strike: strike || undefined, ring: ring || undefined, label, initials: ini, badge, title, movable: canMoveInspection(j, today) || undefined });
  }
  if (j.roll_to && j.roll_to !== j.date && j.roll_to >= from && j.roll_to <= to) {
    out.push({ ...base, key: `insp:${j.id}:roll`, start: j.roll_to, tone: 'bad', dashed: true, ring: true, glyph: '⏰', label: 'missed', initials: ini, title: `Missed inspection from ${md(j.date)} — rolled into today · ${inspStateLabel(j)}`, movable: canMoveInspection(j, today) || undefined });
  }
  return out;
}
// Index: listing → jobs (memoised per layer object by the caller).
export function byListing(items) {
  const m = new Map();
  for (const x of arr(items)) { if (!x || !x.listing_id) continue; if (!m.has(x.listing_id)) m.set(x.listing_id, []); m.get(x.listing_id).push(x); }
  return m;
}

// ── tasks ──────────────────────────────────────────────────────────────────
export const taskDay = (t, today) => (t && t.overdue ? today : t ? t.due_date : null);
export function taskChip(t, today) {
  if (!t || !t.listing_id || !t.due_date) return null;
  const tone = t.overdue || t.priority === 'urgent' ? 'bad' : t.priority === 'high' || t.status === 'blocked' ? 'warn' : t.status === 'in_progress' ? 'accent' : 'info';
  const who = arr(t.assignees).map(a => a.name).join(', ');
  return {
    key: `task:${t.id}`, entity: { kind: 'task', id: t.id, listing_id: t.listing_id, date: t.due_date }, listing_id: t.listing_id,
    start: taskDay(t, today), tone, glyph: '✓', label: String(t.title || '').slice(0, 18),
    initials: arr(t.assignees).map(a => a.initials).filter(Boolean).slice(0, 2),
    ring: t.overdue || undefined,
    title: `Task · ${t.title}${t.overdue ? ` · overdue (due ${md(t.due_date)})` : ''}${who ? ` · ${who}` : ''} · ${t.status.replace('_', ' ')}${t.priority !== 'normal' ? ` · ${t.priority}` : ''}`,
    movable: true,
  };
}
export const tasksDue = (items, today) => arr(items).filter(t => t && (t.overdue || t.due_date === today));

// ── turns (base events) ─────────────────────────────────────────────────────
export const turnsOn = (turns, D) => arr(turns).filter(t => t && t.date === D);
// The server already clamps `unassigned` to today..+7 (stays.clampUnassigned).
export const unassignedCleans = (turns, today) => arr(turns).filter(t => t && t.unassigned && !t.done && t.date >= today && t.date <= addDays(today, UNASSIGNED_HORIZON));
// R115 review: TODAY's same-day cleans not done (the Housekeeping board's rule) — a past same-day turn nobody marked
// cleaned is a stale status, not a home at risk (it read 4, then 77 after ‹ Earlier, vs the board's 1)
export const atRiskCleans = (turns, today) => arr(turns).filter(t => t && t.at_risk && (!today || t.date === today));
export function cleansDay(turns, D) {
  const list = turnsOn(turns, D);
  return { total: list.length, done: list.filter(t => t.done || DONE_TURN.has(t.status)).length, at_risk: list.filter(t => t.at_risk).length, same_day: list.filter(t => t.same_day).length, unassigned: list.filter(t => t.unassigned && !t.done).length };
}

// ── guests (base stays) ─────────────────────────────────────────────────────
// R115 review: the Guest Recovery CASE's state (stay.recovery) wins over the thread's — a case started from the Calendar
// (often with no thread) is open too; the ticket's recovery_status is the fallback for an older server.
export const recoveryOf = (s) => (s && s.recovery && s.recovery.status ? s.recovery
  : (s && s.flagged && s.flagged.recovery_status ? { status: s.flagged.recovery_status, outcome: s.flagged.recovery_outcome || null } : null));
export const isRecoveryOpen = (s) => { const r = recoveryOf(s); return !!(r && r.status && r.status !== 'smoothed'); };
export const isNegativeInHouse = (s) => !!(s && s.negative && s.state === 'in_house');
export function isAtRiskArrival(s, today, days = AT_RISK_ARRIVAL_DAYS) {
  if (!s || s.kind !== 'stay' || s.hidden || !s.checks) return false;
  if (!(s.check_in >= today && s.check_in <= addDays(today, days - 1))) return false;
  return s.checks.rollup === 'at_risk' || s.checks.rollup === 'blocked';
}
export const within48h = (s) => !!(s && s.checks && s.checks.hours_to_checkin != null && s.checks.hours_to_checkin < 48);
export const isAwaitingReview = (s) => !!(s && s.kind === 'stay' && s.review && s.review.status === 'awaiting');
const ROLLUP_TONE = { verified: 'ok', pending: 'warn', at_risk: 'bad', blocked: 'bad', not_generated: 'muted' };
const ROLLUP_LABEL = { verified: 'Arrival verified', pending: 'Arrival checklist pending', at_risk: 'Arrival at risk', blocked: 'Arrival blocked', not_generated: 'No arrival checklist yet' };
const OUTCOME_TONE = { smoothed: 'ok', partial: 'warn', unresolved: 'bad', blocked_owner: 'bad' };
const OUTCOME_ICON = { smoothed: '✓', partial: '◐', unresolved: '✕', blocked_owner: '⊘' };
const OUTCOME_LABEL = { smoothed: 'Smoothed', partial: 'Partly resolved', unresolved: "Couldn't resolve", blocked_owner: 'Blocked by owner' };
// guest_signals: the sentiment tone class + badges on one stay bar.
export function staySignals(s, today) {
  if (!s || s.kind === 'inquiry' || s.hidden) return null;
  const badges = [];
  const f = s.flagged;
  if (isRecoveryOpen(s)) badges.push({ key: 'gs-rec', glyph: '🛟', text: 'Recovery', title: 'Guest recovery open', tone: 'bad' });
  // a smoothed recovery is a happy ending — only the outcomes that still matter show
  else { const r = recoveryOf(s); const oc = r && r.outcome; if (oc && oc !== 'smoothed') badges.push({ key: 'gs-rec', glyph: OUTCOME_ICON[oc] || '✓', title: `Recovery closed — ${OUTCOME_LABEL[oc] || 'Smoothed'}`, tone: OUTCOME_TONE[oc] || 'ok' }); }
  if (s.kind === 'stay' && s.checks && s.check_in >= today) {
    const r = s.checks.rollup;
    const extra = s.checks.required_total ? ` · ${s.checks.required_done}/${s.checks.required_total} required done` : '';
    badges.push({ key: 'gs-ready', glyph: '●', title: `${ROLLUP_LABEL[r] || 'Arrival checklist'}${extra}`, tone: ROLLUP_TONE[r] || 'muted' });
  }
  if (s.review && s.review.status === 'received' && s.review.rating != null) badges.push({ key: 'gs-review', glyph: '★', text: String(s.review.rating), title: `Review ${s.review.rating}★`, tone: s.review.rating <= 3 ? 'bad' : s.review.rating >= 4.5 ? 'ok' : 'warn' });
  else if (isAwaitingReview(s)) badges.push({ key: 'gs-review', glyph: '☆', title: 'Review not in yet', tone: 'muted' });
  const sent = f && f.open ? f.sentiment : null;
  const cls = sent === 'negative' ? 'opv-sent-neg' : sent === 'positive' ? 'opv-sent-pos' : undefined;
  if (!badges.length && !cls) return null;
  return { cls, badges };
}

// ── health ─────────────────────────────────────────────────────────────────
export const openIssuesOf = (row, exclMessages = false) => {
  if (!row || row.open_issues == null) return null;
  return exclMessages ? Math.max(0, row.open_issues - ((row.open_by_source && row.open_by_source.message) || 0)) : row.open_issues;
};
export function healthTotals(byListing, ids) {
  const by = byListing || {};
  let open = 0, excl = 0, hot = 0, homes = 0, known = false;
  let sw = 0, sn = 0, scored = 0, r30 = 0, rsum = 0, rrated = 0;
  for (const id of ids) {
    const r = by[id];
    if (!r) continue;
    if (r.open_issues != null) { known = true; open += r.open_issues; excl += openIssuesOf(r, true); hot += r.open_hot || 0; if (r.open_issues > 0) homes++; }
    if (r.score != null && r.score_n) { sw += r.score * r.score_n; sn += r.score_n; scored++; }
    if (r.reviews_30d != null) { r30 += r.reviews_30d; if (r.reviews_30d_avg != null) { rsum += r.reviews_30d_avg * r.reviews_30d; rrated += r.reviews_30d; } }
  }
  return {
    open: known ? open : null, open_excl_messages: known ? excl : null, hot: known ? hot : null, homes_with_open: homes,
    avg_score: sn ? Math.round((sw / sn) * 100) / 100 : null, score_n: sn, scored_homes: scored,
    reviews_30d: r30, reviews_30d_avg: rrated ? Math.round((rsum / rrated) * 100) / 100 : null,
  };
}
export function healthBadges(row, lid) {
  if (!row) return [];
  const out = [];
  const entity = { kind: 'listing', id: lid };
  if (row.open_issues) out.push({ key: 'h-iss', glyph: '⚑', text: String(row.open_issues), tone: row.open_hot ? 'bad' : 'warn', entity,
    title: `${row.open_issues} open issue${row.open_issues === 1 ? '' : 's'} (30 days)${row.open_hot ? ` · ${row.open_hot} high/emergency` : ''}${row.open_by_source && row.open_by_source.message ? ` · ${row.open_by_source.message} from guest messages` : ''}` });
  if (row.supply_open) out.push({ key: 'h-sup', glyph: '📦', text: String(row.supply_open), tone: 'warn', entity, title: `${row.supply_open} supply request${row.supply_open === 1 ? '' : 's'} not ordered yet` });
  if (row.last_grade != null) out.push({ key: 'h-grade', glyph: '🔍', text: String(row.last_grade), tone: row.last_grade < 70 ? 'bad' : row.last_grade < 85 ? 'warn' : 'ok', entity, title: `Last inspection grade ${row.last_grade}${row.days_since_inspection != null ? ` · ${row.days_since_inspection}d ago` : ''}` });
  return out;
}

// ── the portfolio day (inspector 'day') ───────────────────────────────────
export function dayAgenda({ D, today, data, jobs, tasks }) {
  const ev = (data && data.events) || {};
  const turns = turnsOn(ev.turns, D).slice().sort((a, b) => (Number(!!b.unassigned && !b.done) - Number(!!a.unassigned && !a.done)) || (Number(!!b.at_risk) - Number(!!a.at_risk)) || (Number(!!b.same_day) - Number(!!a.same_day)) || String(a.listing_id).localeCompare(String(b.listing_id)));
  const insp = jobs ? jobsOnDay(jobs, D).sort((a, b) => (Number(b.state === 'unassigned') - Number(a.state === 'unassigned')) || (Number(!!b.missed) - Number(!!a.missed)) || a.listing_id.localeCompare(b.listing_id)) : null;
  const wos = arr(ev.work_orders).filter(w => w && (w.date === D || (w.end && w.date <= D && D <= w.end)));
  const tk = tasks ? arr(tasks).filter(t => t && (t.due_date === D || (D === today && t.overdue))) : null;
  const stays = arr(data && data.stays).filter(s => s && s.kind !== 'inquiry' && !s.hidden);
  const c = cleansDay(ev.turns, D);
  return {
    D, turns, inspections: insp, wos, tasks: tk,
    totals: {
      cleans: c.total, cleans_done: c.done, cleans_unassigned: c.unassigned, at_risk: c.at_risk, same_day: c.same_day,
      inspections: insp ? insp.length : null, inspections_unassigned: insp ? insp.filter(j => j.state === 'unassigned').length : null,
      wos: wos.length, tasks: tk ? tk.length : null,
      arrivals: stays.filter(s => s.check_in === D).length, departures: stays.filter(s => s.check_out === D).length,
    },
  };
}

// ── the date chooser ───────────────────────────────────────────────────────
// true = offer it; a string = why not (shown when the page lists disabled ones).
export function chooserEnabled(kind, cell, today) {
  const d = cell && cell.date;
  if (!isDay(d)) return 'Pick a day';
  if (kind === 'clean' || kind === 'task') return d >= today ? true : 'That day has passed';
  if (kind === 'inspection') {
    if (d < today) return 'That day has passed';
    if (d > addDays(today, INSPECTION_MAX_DAYS)) return `Inspections can be scheduled up to ${INSPECTION_MAX_DAYS} days out`;
    return true;
  }
  return 'Unknown';
}
export function guestInside(stays, lid, date) {
  return arr(stays).some(s => s && s.listing_id === lid && s.kind !== 'inquiry' && !s.hidden && s.check_in < date && date < s.check_out);
}
export const existingClean = (turns, lid, date) => arr(turns).find(t => t && t.listing_id === lid && t.date === date && !t.done) || null;
export const existingInspection = (jobs, lid, date) => arr(jobs).find(j => j && j.key === `${lid}|${date}` && !isGhost(j)) || null;
const clip = (s, n) => String(s == null ? '' : s).trim().slice(0, n);
// POST /api/assetops/turns
export function cleanBody(f, by) {
  const b = { listing_id: String(f.listing_id), date: f.date, type: TURN_TYPES.some(([k]) => k === f.type) ? f.type : 'turnover', by: clip(by, 80) || 'office', source: 'calendar' };
  const note = clip(f.note, 500);
  if (note) b.note = note;
  if (f.confirm_recent) b.confirm_recent = true;
  return b;
}
// POST /api/assetops/inspections/assign — manual (📌) ONLY when no job of any kind sits on that key and no live checkout
// backs it (R115 review: stamping manual on a checkout / pre-arrival / ↪ moved job made it outlive a cancelled booking
// as a 📌 and turned off the R114 orphan rule). f.backed: a live checkout on that day is known (the form checks stays).
export function inspBody(f, existing) {
  const b = { listing_id: String(f.listing_id), checkout: f.date, inspector: clip(f.inspector, 80), source: 'calendar' };
  if (!existing && !f.backed) b.manual = true;
  if (f.override) b.override = true;
  return b;
}
// POST /api/tasks
export function taskBody(f) {
  const b = {
    title: clip(f.title, 200), description: clip(f.description, 4000), status: 'todo',
    priority: TASK_PRIORITIES.some(([k]) => k === f.priority) ? f.priority : 'normal',
    assignees: [...new Set(arr(f.assignees).map(a => String(a).trim()).filter(Boolean))],
    due_date: f.date, listing_id: String(f.listing_id), listing_name: clip(f.listing_name, 120), source: 'calendar',
  };
  if (f.stay_id) b.link = { kind: 'reservation', id: String(f.stay_id) };
  return b;
}

// ── Plan cleans (GET /housekeeping/schedule/propose → POST /apply) ─────────
export function planRows(propose) {
  const out = [];
  for (const d of arr(propose && propose.days)) {
    for (const r of arr(d.turns)) {
      if (!r || r.locked) continue;
      out.push({ turn_id: String(r.turn_id), listing_id: String(r.listing_id || ''), listing: String(r.listing || ''), date: d.date, same_day: !!r.same_day,
        cleaner_id: r.proposed_cleaner_id ? String(r.proposed_cleaner_id) : null, cleaner: r.proposed_cleaner || null, reason: r.reason || null, est_minutes: r.est_minutes || null });
    }
  }
  return out;
}
export function applyBody(rows, picked, override = false) {
  const set = picked instanceof Set ? picked : new Set(arr(picked));
  const assignments = arr(rows).filter(r => r.cleaner_id && set.has(r.turn_id)).map(r => ({ turn_id: r.turn_id, cleaner_id: r.cleaner_id }));
  return override ? { assignments, override: true } : { assignments };
}
